import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
  type ThunkDispatch,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { ApiError } from '../../api/generated/core/ApiError';
import { ShipmentTrackerService } from '../../api/generated/services/ShipmentTrackerService';
import { ShipmentDocumentCustomService } from '../../api/customServices/ShipmentDocumentCustomService';
import type { ShipmentListItemView } from '../../api/generated/models/ShipmentListItemView';
import type { ShipmentDetailView } from '../../api/generated/models/ShipmentDetailView';
import type { ShipmentItemView } from '../../api/generated/models/ShipmentItemView';
import type { ShipmentActivityView } from '../../api/generated/models/ShipmentActivityView';
import type { ShipmentCommentView } from '../../api/generated/models/ShipmentCommentView';
import type { ShipmentDocumentView } from '../../api/generated/models/ShipmentDocumentView';
import type { ShipmentTransportProfileView } from '../../api/generated/models/ShipmentTransportProfileView';
import type { ShipmentIncotermView } from '../../api/generated/models/ShipmentIncotermView';
import type { ShipmentCountryView } from '../../api/generated/models/ShipmentCountryView';
import type { ShipmentCreateRequest } from '../../api/generated/models/ShipmentCreateRequest';
import type { ShipmentItemRequest } from '../../api/generated/models/ShipmentItemRequest';
import type { ShipmentCostBreakdownView } from '../../api/generated/models/ShipmentCostBreakdownView';
import type { ShipmentCostsUpdateRequest } from '../../api/generated/models/ShipmentCostsUpdateRequest';
import type { ShipmentCostComponentRequest } from '../../api/generated/models/ShipmentCostComponentRequest';
import type { ShipmentAllocationMethodRequest } from '../../api/generated/models/ShipmentAllocationMethodRequest';
import type { ShipmentRecentActivityView } from '../../api/generated/models/ShipmentRecentActivityView';
import type { ShipmentArchiveListItemView } from '../../api/generated/models/ShipmentArchiveListItemView';
import type { ShipmentStatusRequest } from '../../api/generated/models/ShipmentStatusRequest';
import type { ShipmentLabelView } from '../../api/generated/models/ShipmentLabelView';
import type { ShipmentLabelRequest } from '../../api/generated/models/ShipmentLabelRequest';
import type { ShipmentLabelUpdateRequest } from '../../api/generated/models/ShipmentLabelUpdateRequest';
import type { ShipmentTrackerSettingsView } from '../../api/generated/models/ShipmentTrackerSettingsView';
import type { ShipmentTrackerSettingsRequest } from '../../api/generated/models/ShipmentTrackerSettingsRequest';
import type { ShipmentTransportProfileRequest } from '../../api/generated/models/ShipmentTransportProfileRequest';
import { addNotification } from './notificationSlice';
import i18next from 'i18next';

// Draft of a not-yet-saved shipment's header fields. All strings so they map
// straight to the create-form inputs; converted to the request shape on save.
export interface ShipmentDraft {
  name: string;
  currency: string;
  transportProfileId: string;
  incoterm: string;
  originCountryCode: string;
  destinationCountryCode: string;
  originCity: string;
  destinationCity: string;
  eta: string;
  warehouseComment: string;
  plannedTransport: string;
  plannedDuty: string;
  plannedClearance: string;
  plannedEnergyTax: string;
  labelIds: string[];
  trackingUrl: string;
}

export const emptyDraftShipment: ShipmentDraft = {
  name: '',
  currency: 'EUR',
  transportProfileId: '',
  incoterm: '',
  originCountryCode: '',
  destinationCountryCode: '',
  originCity: '',
  destinationCity: '',
  eta: '',
  warehouseComment: '',
  plannedTransport: '',
  plannedDuty: '',
  plannedClearance: '',
  plannedEnergyTax: '',
  labelIds: [],
  trackingUrl: '',
};

export interface ShipmentTrackerState {
  list: {
    items: ShipmentListItemView[];
    total: number;
    page: number;
    limit: number;
    statusFilter: string[];
    loading: boolean;
    error: string | null;
  };
  detail: {
    shipment: ShipmentDetailView | null;
    loading: boolean;
    error: string | null;
  };
  dropdowns: {
    transportProfiles: ShipmentTransportProfileView[];
    incoterms: ShipmentIncotermView[];
    countries: ShipmentCountryView[];
    loaded: boolean;
  };
  // Local staging for items on a not-yet-saved shipment (workflowStatus: draft before first POST)
  pendingItems: ShipmentItemRequest[];
  // Header fields of a not-yet-saved shipment, kept in Redux so they survive
  // navigation to the item form and back (local component state would be lost).
  draftShipment: ShipmentDraft;
  activity: {
    items: ShipmentActivityView[];
    loading: boolean;
  };
  comments: {
    items: ShipmentCommentView[];
    loading: boolean;
    posting: boolean;
  };
  documents: {
    items: ShipmentDocumentView[];
    loading: boolean;
    uploading: boolean;
  };
  costBreakdown: {
    data: ShipmentCostBreakdownView | null;
    loading: boolean;
    saving: boolean;
    error: string | null;
  };
  dashboard: {
    recentActivity: ShipmentRecentActivityView[];
    loading: boolean;
    error: string | null;
  };
  statusBoard: {
    columns: Record<string, ShipmentListItemView[]>;
    loading: boolean;
    error: string | null;
  };
  archive: {
    items: ShipmentArchiveListItemView[];
    total: number;
    page: number;
    limit: number;
    statusFilter: string[];
    order: 'desc' | 'asc';
    search: string;
    loading: boolean;
    error: string | null;
  };
  labels: {
    items: ShipmentLabelView[];
    loading: boolean;
    loaded: boolean;
  };
  settings: {
    data: ShipmentTrackerSettingsView | null;
    loading: boolean;
    loaded: boolean;
    saving: boolean;
  };
}

const initialState: ShipmentTrackerState = {
  list: {
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    statusFilter: [],
    loading: false,
    error: null,
  },
  detail: {
    shipment: null,
    loading: false,
    error: null,
  },
  dropdowns: {
    transportProfiles: [],
    incoterms: [],
    countries: [],
    loaded: false,
  },
  pendingItems: [],
  draftShipment: { ...emptyDraftShipment },
  activity: {
    items: [],
    loading: false,
  },
  comments: {
    items: [],
    loading: false,
    posting: false,
  },
  documents: {
    items: [],
    loading: false,
    uploading: false,
  },
  costBreakdown: {
    data: null,
    loading: false,
    saving: false,
    error: null,
  },
  dashboard: {
    recentActivity: [],
    loading: false,
    error: null,
  },
  statusBoard: {
    columns: {},
    loading: false,
    error: null,
  },
  archive: {
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    statusFilter: [],
    order: 'desc',
    search: '',
    loading: false,
    error: null,
  },
  labels: {
    items: [],
    loading: false,
    loaded: false,
  },
  settings: {
    data: null,
    loading: false,
    loaded: false,
    saving: false,
  },
};

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const fetchShipmentList = createAsyncThunk(
  'shipmentTracker/fetchList',
  async (
    params: { page?: number; limit?: number; status?: string[] },
    { rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.getAppShipmentTrackerList(
        params.status?.length ? params.status.join(',') : undefined,
        params.page ?? 1,
        params.limit ?? 20,
      );
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

export const fetchShipmentDetail = createAsyncThunk(
  'shipmentTracker/fetchDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.getAppShipmentTrackerGet(id);
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

export const createShipment = createAsyncThunk(
  'shipmentTracker/create',
  async (data: ShipmentCreateRequest, { dispatch, rejectWithValue }) => {
    try {
      const created =
        await ShipmentTrackerService.postAppShipmentTrackerCreate(data);
      refreshCatalogUsageCounts(dispatch);
      return created;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const updateShipment = createAsyncThunk(
  'shipmentTracker/update',
  async (
    { id, data }: { id: string; data: ShipmentCreateRequest },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const updated = await ShipmentTrackerService.putAppShipmentTrackerUpdate(
        id,
        data,
      );
      refreshCatalogUsageCounts(dispatch);
      return updated;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const deleteShipment = createAsyncThunk(
  'shipmentTracker/delete',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      await ShipmentTrackerService.deleteAppShipmentTrackerDelete(id);
      refreshCatalogUsageCounts(dispatch);
      return id;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const addShipmentItem = createAsyncThunk(
  'shipmentTracker/addItem',
  async (
    { shipmentId, data }: { shipmentId: string; data: ShipmentItemRequest },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.postAppShipmentTrackerItemAdd(
        shipmentId,
        data,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const updateShipmentItem = createAsyncThunk(
  'shipmentTracker/updateItem',
  async (
    {
      shipmentId,
      itemId,
      data,
    }: { shipmentId: string; itemId: string; data: ShipmentItemRequest },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.patchAppShipmentTrackerItemUpdate(
        shipmentId,
        itemId,
        data,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const deleteShipmentItem = createAsyncThunk(
  'shipmentTracker/deleteItem',
  async (
    { shipmentId, itemId }: { shipmentId: string; itemId: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      await ShipmentTrackerService.deleteAppShipmentTrackerItemDelete(
        shipmentId,
        itemId,
      );
      return itemId;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const fetchShipmentLabels = createAsyncThunk(
  'shipmentTracker/fetchLabels',
  async (_, { rejectWithValue }) => {
    try {
      const res =
        await ShipmentTrackerService.getAppShipmentTrackerLabelsList();
      return res.items ?? [];
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

export const createShipmentLabel = createAsyncThunk(
  'shipmentTracker/createLabel',
  async (data: ShipmentLabelRequest, { dispatch, rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.postAppShipmentTrackerLabelsCreate(
        data,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const updateShipmentLabel = createAsyncThunk(
  'shipmentTracker/updateLabel',
  async (
    { id, data }: { id: string; data: ShipmentLabelUpdateRequest },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.patchAppShipmentTrackerLabelsUpdate(
        id,
        data,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const deleteShipmentLabel = createAsyncThunk(
  'shipmentTracker/deleteLabel',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      await ShipmentTrackerService.deleteAppShipmentTrackerLabelsDelete(id);
      return id;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const fetchShipmentTrackerSettings = createAsyncThunk(
  'shipmentTracker/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.getAppShipmentTrackerSettingsGet();
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

export const saveShipmentTrackerSettings = createAsyncThunk(
  'shipmentTracker/saveSettings',
  async (data: ShipmentTrackerSettingsRequest, { dispatch, rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.putAppShipmentTrackerSettingsPut(data);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const createCustomTransportProfile = createAsyncThunk(
  'shipmentTracker/createTransportProfile',
  async (data: ShipmentTransportProfileRequest, { dispatch, rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.postAppShipmentTrackerTransportProfilesCreate(
        data,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const updateCustomTransportProfile = createAsyncThunk(
  'shipmentTracker/updateTransportProfile',
  async (
    { id, data }: { id: string; data: ShipmentTransportProfileRequest },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.patchAppShipmentTrackerTransportProfilesUpdate(
        id,
        data,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const deleteCustomTransportProfile = createAsyncThunk(
  'shipmentTracker/deleteTransportProfile',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      await ShipmentTrackerService.deleteAppShipmentTrackerTransportProfilesDelete(
        id,
      );
      return id;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

/** Dropdowns + labels + settings for Shipment Tracker entry. */
export const fetchShipmentTrackerBootstrap = createAsyncThunk(
  'shipmentTracker/bootstrap',
  async (_, { dispatch, getState }) => {
    const state = getState() as { shipmentTracker: ShipmentTrackerState };
    const tasks: Promise<unknown>[] = [];
    if (!state.shipmentTracker.dropdowns.loaded) {
      tasks.push(dispatch(fetchDropdowns()).unwrap());
    }
    if (!state.shipmentTracker.labels.loaded) {
      tasks.push(dispatch(fetchShipmentLabels()).unwrap());
    }
    if (!state.shipmentTracker.settings.loaded) {
      tasks.push(dispatch(fetchShipmentTrackerSettings()).unwrap());
    }
    await Promise.all(tasks);
  },
);

export const fetchDropdowns = createAsyncThunk(
  'shipmentTracker/fetchDropdowns',
  async (_, { rejectWithValue }) => {
    try {
      const [transportProfiles, incoterms, countries] = await Promise.all([
        ShipmentTrackerService.getAppShipmentTrackerTransportProfiles(),
        ShipmentTrackerService.getAppShipmentTrackerIncoterms(),
        ShipmentTrackerService.getAppShipmentTrackerCountries(),
      ]);
      return { transportProfiles, incoterms, countries };
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

/** Label/transport list usageCount; call after shipment assign/unassign/delete/archive. */
function refreshCatalogUsageCounts(
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
): void {
  void dispatch(fetchShipmentLabels());
  void dispatch(fetchDropdowns());
}

export const fetchShipmentActivity = createAsyncThunk(
  'shipmentTracker/fetchActivity',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.getAppShipmentTrackerActivity(id);
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

export const fetchShipmentComments = createAsyncThunk(
  'shipmentTracker/fetchComments',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.getAppShipmentTrackerCommentList(id);
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

export const postShipmentComment = createAsyncThunk(
  'shipmentTracker/postComment',
  async (
    { id, body }: { id: string; body: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.postAppShipmentTrackerCommentAdd(id, {
        body,
      });
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const fetchShipmentDocuments = createAsyncThunk(
  'shipmentTracker/fetchDocuments',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.getAppShipmentTrackerDocumentList(id);
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

export const uploadShipmentDocument = createAsyncThunk(
  'shipmentTracker/uploadDocument',
  async (
    { id, file }: { id: string; file: File },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentDocumentCustomService.upload(id, file);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const deleteShipmentDocument = createAsyncThunk(
  'shipmentTracker/deleteDocument',
  async (
    { id, docId }: { id: string; docId: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      await ShipmentTrackerService.deleteAppShipmentTrackerDocumentDelete(
        id,
        docId,
      );
      return docId;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const fetchCostBreakdown = createAsyncThunk(
  'shipmentTracker/fetchCostBreakdown',
  async (id: string, { rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.getAppShipmentTrackerCostBreakdown(
        id,
      );
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

// Every cost mutation returns the recalculated breakdown, so each thunk below feeds the same
// costBreakdown slice and the tab never has to re-fetch.

export const updateCosts = createAsyncThunk(
  'shipmentTracker/updateCosts',
  async (
    { id, data }: { id: string; data: ShipmentCostsUpdateRequest },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.patchAppShipmentTrackerCostsPatch(
        id,
        data,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const updateAllocationMethod = createAsyncThunk(
  'shipmentTracker/updateAllocationMethod',
  async (
    { id, allocationMethod }: { id: string; allocationMethod: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.patchAppShipmentTrackerAllocationMethod(
        id,
        {
          allocationMethod,
        } as ShipmentAllocationMethodRequest,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const addCostComponent = createAsyncThunk(
  'shipmentTracker/addCostComponent',
  async (
    { id, data }: { id: string; data: ShipmentCostComponentRequest },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.postAppShipmentTrackerCostComponentAdd(
        id,
        data,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const updateCostComponent = createAsyncThunk(
  'shipmentTracker/updateCostComponent',
  async (
    {
      id,
      componentId,
      data,
    }: { id: string; componentId: string; data: ShipmentCostComponentRequest },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.patchAppShipmentTrackerCostComponentUpdate(
        id,
        componentId,
        data,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const deleteCostComponent = createAsyncThunk(
  'shipmentTracker/deleteCostComponent',
  async (
    { id, componentId }: { id: string; componentId: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.deleteAppShipmentTrackerCostComponentDelete(
        id,
        componentId,
      );
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const fetchDashboard = createAsyncThunk(
  'shipmentTracker/fetchDashboard',
  async (
    params: { status?: string[]; page?: number; limit?: number },
    { rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.getAppShipmentTrackerDashboard(
        params.status?.length ? params.status.join(',') : undefined,
        params.page ?? 1,
        params.limit ?? 20,
      );
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

export const fetchStatusBoard = createAsyncThunk(
  'shipmentTracker/fetchStatusBoard',
  async (_, { rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.getAppShipmentTrackerStatusBoard();
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

export const changeShipmentStatus = createAsyncThunk(
  'shipmentTracker/changeStatus',
  async (
    {
      id,
      executionStatus,
    }: { id: string; executionStatus: ShipmentStatusRequest.executionStatus },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.patchAppShipmentTrackerStatus(id, {
        executionStatus,
      });
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const archiveShipment = createAsyncThunk(
  'shipmentTracker/archive',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      await ShipmentTrackerService.postAppShipmentTrackerArchiveCreate(id);
      refreshCatalogUsageCounts(dispatch);
      return id;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const fetchArchiveList = createAsyncThunk(
  'shipmentTracker/fetchArchiveList',
  async (
    params: {
      status?: string[];
      q?: string;
      order?: string;
      page?: number;
      limit?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await ShipmentTrackerService.getAppShipmentTrackerArchiveList(
        params.status?.length ? params.status.join(',') : undefined,
        params.q,
        params.order ?? 'desc',
        params.page ?? 1,
        params.limit ?? 20,
      );
    } catch (e) {
      return rejectWithValue(e instanceof ApiError ? e.body : String(e));
    }
  },
);

export const restoreShipment = createAsyncThunk(
  'shipmentTracker/restore',
  async (archiveId: string, { dispatch, rejectWithValue }) => {
    try {
      await ShipmentTrackerService.postAppShipmentTrackerArchiveRestore(
        archiveId,
      );
      refreshCatalogUsageCounts(dispatch);
      return archiveId;
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

export const restoreAllShipments = createAsyncThunk(
  'shipmentTracker/restoreAll',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      return await ShipmentTrackerService.postAppShipmentTrackerArchiveRestoreAll();
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? (e.body?.error ?? i18next.t('error'))
          : String(e);
      dispatch(
        addNotification({
          type: 'error',
          title: i18next.t('error'),
          message: msg,
        }),
      );
      return rejectWithValue(msg);
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const shipmentTrackerSlice = createSlice({
  name: 'shipmentTracker',
  initialState,
  reducers: {
    setListPage(state, action: PayloadAction<number>) {
      state.list.page = action.payload;
    },
    setListStatusFilter(state, action: PayloadAction<string[]>) {
      state.list.statusFilter = action.payload;
      state.list.page = 1;
    },
    clearDetail(state) {
      state.detail.shipment = null;
      state.detail.error = null;
      state.activity.items = [];
      state.activity.loading = false;
      state.comments.items = [];
      state.comments.loading = false;
      state.comments.posting = false;
      state.documents.items = [];
      state.documents.loading = false;
      state.documents.uploading = false;
      state.costBreakdown.data = null;
      state.costBreakdown.error = null;
    },
    // Pending item staging (used before the shipment has an id)
    addPendingItem(state, action: PayloadAction<ShipmentItemRequest>) {
      state.pendingItems.push(action.payload);
    },
    updatePendingItem(
      state,
      action: PayloadAction<{ index: number; data: ShipmentItemRequest }>,
    ) {
      state.pendingItems[action.payload.index] = action.payload.data;
    },
    removePendingItem(state, action: PayloadAction<number>) {
      state.pendingItems.splice(action.payload, 1);
    },
    clearPendingItems(state) {
      state.pendingItems = [];
    },
    // Draft header fields for a not-yet-saved shipment
    patchDraftShipment(state, action: PayloadAction<Partial<ShipmentDraft>>) {
      state.draftShipment = { ...state.draftShipment, ...action.payload };
    },
    /** Starts a genuinely blank shipment: header fields and any staged items. */
    resetDraftShipment(state) {
      state.draftShipment = { ...emptyDraftShipment };
      state.pendingItems = [];
    },
    setArchivePage(state, action: PayloadAction<number>) {
      state.archive.page = action.payload;
    },
    setArchiveStatusFilter(state, action: PayloadAction<string[]>) {
      state.archive.statusFilter = action.payload;
      state.archive.page = 1;
    },
    setArchiveOrder(state, action: PayloadAction<'desc' | 'asc'>) {
      state.archive.order = action.payload;
      state.archive.page = 1;
    },
    setArchiveSearch(state, action: PayloadAction<string>) {
      state.archive.search = action.payload;
      state.archive.page = 1;
    },
  },
  extraReducers: (builder) => {
    // ── List ──
    builder
      .addCase(fetchShipmentList.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchShipmentList.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.items = action.payload.items ?? [];
        state.list.total = action.payload.total ?? 0;
      })
      .addCase(fetchShipmentList.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = String(action.payload ?? action.error.message);
      });

    // ── Detail ──
    builder
      .addCase(fetchShipmentDetail.pending, (state) => {
        state.detail.loading = true;
        state.detail.error = null;
      })
      .addCase(fetchShipmentDetail.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.shipment = action.payload;
      })
      .addCase(fetchShipmentDetail.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error = String(action.payload ?? action.error.message);
      });

    // ── Create → update detail ──
    builder.addCase(createShipment.fulfilled, (state, action) => {
      state.detail.shipment = action.payload;
    });

    // ── Update → update detail ──
    builder.addCase(updateShipment.fulfilled, (state, action) => {
      state.detail.shipment = action.payload;
    });

    // ── Add item → patch detail.items ──
    builder.addCase(addShipmentItem.fulfilled, (state, action) => {
      const item: ShipmentItemView = action.payload;
      if (state.detail.shipment) {
        state.detail.shipment.items = [
          ...(state.detail.shipment.items ?? []),
          item,
        ];
      }
    });

    // ── Update item → patch detail.items ──
    builder.addCase(updateShipmentItem.fulfilled, (state, action) => {
      const updated: ShipmentItemView = action.payload;
      if (state.detail.shipment?.items) {
        state.detail.shipment.items = state.detail.shipment.items.map((it) =>
          it.id === updated.id ? updated : it,
        );
      }
    });

    // ── Delete item → remove from detail.items ──
    builder.addCase(deleteShipmentItem.fulfilled, (state, action) => {
      const itemId = action.payload;
      if (state.detail.shipment?.items) {
        state.detail.shipment.items = state.detail.shipment.items.filter(
          (it) => it.id !== itemId,
        );
      }
    });

    // ── Dropdowns ──
    builder.addCase(fetchDropdowns.fulfilled, (state, action) => {
      state.dropdowns.transportProfiles = action.payload.transportProfiles;
      state.dropdowns.incoterms = action.payload.incoterms;
      state.dropdowns.countries = action.payload.countries;
      state.dropdowns.loaded = true;
    });

    builder
      .addCase(fetchShipmentLabels.pending, (state) => {
        state.labels.loading = true;
      })
      .addCase(fetchShipmentLabels.fulfilled, (state, action) => {
        state.labels.loading = false;
        state.labels.loaded = true;
        state.labels.items = action.payload;
      })
      .addCase(fetchShipmentLabels.rejected, (state) => {
        state.labels.loading = false;
      });

    builder.addCase(createShipmentLabel.fulfilled, (state, action) => {
      const idx = state.labels.items.findIndex((l) => l.id === action.payload.id);
      if (idx >= 0) {
        state.labels.items[idx] = action.payload;
      } else {
        state.labels.items.push(action.payload);
        state.labels.items.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    builder.addCase(updateShipmentLabel.fulfilled, (state, action) => {
      state.labels.items = state.labels.items
        .map((l) => (l.id === action.payload.id ? action.payload : l))
        .sort((a, b) => a.name.localeCompare(b.name));
    });

    builder.addCase(deleteShipmentLabel.fulfilled, (state, action) => {
      state.labels.items = state.labels.items.filter((l) => l.id !== action.payload);
    });

    builder
      .addCase(fetchShipmentTrackerSettings.pending, (state) => {
        state.settings.loading = true;
      })
      .addCase(fetchShipmentTrackerSettings.fulfilled, (state, action) => {
        state.settings.loading = false;
        state.settings.loaded = true;
        state.settings.data = action.payload;
      })
      .addCase(fetchShipmentTrackerSettings.rejected, (state) => {
        state.settings.loading = false;
      });

    builder
      .addCase(saveShipmentTrackerSettings.pending, (state) => {
        state.settings.saving = true;
      })
      .addCase(saveShipmentTrackerSettings.fulfilled, (state, action) => {
        state.settings.saving = false;
        state.settings.data = action.payload;
      })
      .addCase(saveShipmentTrackerSettings.rejected, (state) => {
        state.settings.saving = false;
      });

    builder.addCase(createCustomTransportProfile.fulfilled, (state, action) => {
      state.dropdowns.transportProfiles = [
        ...state.dropdowns.transportProfiles,
        action.payload,
      ];
    });

    builder.addCase(updateCustomTransportProfile.fulfilled, (state, action) => {
      state.dropdowns.transportProfiles = state.dropdowns.transportProfiles.map(
        (p) => (p.id === action.payload.id ? action.payload : p),
      );
    });

    builder.addCase(deleteCustomTransportProfile.fulfilled, (state, action) => {
      state.dropdowns.transportProfiles = state.dropdowns.transportProfiles.filter(
        (p) => p.id !== action.payload,
      );
    });

    // ── Activity ──
    builder
      .addCase(fetchShipmentActivity.pending, (state) => {
        state.activity.loading = true;
      })
      .addCase(fetchShipmentActivity.fulfilled, (state, action) => {
        state.activity.loading = false;
        state.activity.items = action.payload ?? [];
      })
      .addCase(fetchShipmentActivity.rejected, (state) => {
        state.activity.loading = false;
      });

    // ── Comments ──
    builder
      .addCase(fetchShipmentComments.pending, (state) => {
        state.comments.loading = true;
      })
      .addCase(fetchShipmentComments.fulfilled, (state, action) => {
        state.comments.loading = false;
        state.comments.items = action.payload ?? [];
      })
      .addCase(fetchShipmentComments.rejected, (state) => {
        state.comments.loading = false;
      });

    builder
      .addCase(postShipmentComment.pending, (state) => {
        state.comments.posting = true;
      })
      .addCase(postShipmentComment.fulfilled, (state, action) => {
        state.comments.posting = false;
        state.comments.items.unshift(action.payload);
      })
      .addCase(postShipmentComment.rejected, (state) => {
        state.comments.posting = false;
      });

    // ── Documents ──
    builder
      .addCase(fetchShipmentDocuments.pending, (state) => {
        state.documents.loading = true;
      })
      .addCase(fetchShipmentDocuments.fulfilled, (state, action) => {
        state.documents.loading = false;
        state.documents.items = action.payload ?? [];
      })
      .addCase(fetchShipmentDocuments.rejected, (state) => {
        state.documents.loading = false;
      });

    builder
      .addCase(uploadShipmentDocument.pending, (state) => {
        state.documents.uploading = true;
      })
      .addCase(uploadShipmentDocument.fulfilled, (state, action) => {
        state.documents.uploading = false;
        state.documents.items.unshift(action.payload);
      })
      .addCase(uploadShipmentDocument.rejected, (state) => {
        state.documents.uploading = false;
      });

    builder.addCase(deleteShipmentDocument.fulfilled, (state, action) => {
      state.documents.items = state.documents.items.filter(
        (d) => d.id !== action.payload,
      );
    });

    // ── Cost breakdown ──
    builder
      .addCase(fetchCostBreakdown.pending, (state) => {
        state.costBreakdown.loading = true;
        state.costBreakdown.error = null;
      })
      .addCase(fetchCostBreakdown.fulfilled, (state, action) => {
        state.costBreakdown.loading = false;
        state.costBreakdown.data = action.payload;
      })
      .addCase(fetchCostBreakdown.rejected, (state, action) => {
        state.costBreakdown.loading = false;
        state.costBreakdown.error = String(
          action.payload ?? action.error.message,
        );
      });

    // ── Cost mutations ── every one returns the recalculated breakdown, so they all land
    // in costBreakdown.data and the tab stays in sync without re-fetching.
    [
      updateCosts,
      updateAllocationMethod,
      addCostComponent,
      updateCostComponent,
      deleteCostComponent,
    ].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.costBreakdown.saving = true;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.costBreakdown.saving = false;
          state.costBreakdown.data = action.payload;
        })
        .addCase(thunk.rejected, (state) => {
          state.costBreakdown.saving = false;
        });
    });

    // ── Dashboard ──
    builder
      .addCase(fetchDashboard.pending, (state) => {
        state.dashboard.loading = true;
        state.dashboard.error = null;
      })
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        state.dashboard.loading = false;
        state.dashboard.recentActivity = action.payload.recentActivity ?? [];
      })
      .addCase(fetchDashboard.rejected, (state, action) => {
        state.dashboard.loading = false;
        state.dashboard.error = String(action.payload ?? action.error.message);
      });

    // ── Status board ──
    builder
      .addCase(fetchStatusBoard.pending, (state) => {
        state.statusBoard.loading = true;
        state.statusBoard.error = null;
      })
      .addCase(fetchStatusBoard.fulfilled, (state, action) => {
        state.statusBoard.loading = false;
        state.statusBoard.columns = action.payload.columns ?? {};
      })
      .addCase(fetchStatusBoard.rejected, (state, action) => {
        state.statusBoard.loading = false;
        state.statusBoard.error = String(
          action.payload ?? action.error.message,
        );
      });

    // Status change → update board column
    builder.addCase(changeShipmentStatus.fulfilled, (state, action) => {
      const updated = action.payload;
      const newStatus = updated.executionStatus ?? '';
      // Remove from all columns then add to new one
      for (const col of Object.keys(state.statusBoard.columns)) {
        state.statusBoard.columns[col] = state.statusBoard.columns[col].filter(
          (s) => s.id !== updated.id,
        );
      }
      if (newStatus && !state.statusBoard.columns[newStatus]) {
        state.statusBoard.columns[newStatus] = [];
      }
      if (newStatus) {
        state.statusBoard.columns[newStatus].push({
          id: updated.id,
          name: updated.name,
          reference: updated.reference,
          currency: updated.currency,
          workflowStatus: updated.workflowStatus,
          executionStatus: updated.executionStatus,
          createdAt: updated.createdAt,
          eta: updated.eta,
        });
      }
    });

    // Archive → remove from board
    builder.addCase(archiveShipment.fulfilled, (state, action) => {
      const id = action.payload;
      for (const col of Object.keys(state.statusBoard.columns)) {
        state.statusBoard.columns[col] = state.statusBoard.columns[col].filter(
          (s) => s.id !== id,
        );
      }
    });

    // ── Archive list ──
    builder
      .addCase(fetchArchiveList.pending, (state) => {
        state.archive.loading = true;
        state.archive.error = null;
      })
      .addCase(fetchArchiveList.fulfilled, (state, action) => {
        state.archive.loading = false;
        state.archive.items = action.payload.items ?? [];
        state.archive.total = action.payload.total ?? 0;
      })
      .addCase(fetchArchiveList.rejected, (state, action) => {
        state.archive.loading = false;
        state.archive.error = String(action.payload ?? action.error.message);
      });

    // Restore single → remove from archive list
    builder.addCase(restoreShipment.fulfilled, (state, action) => {
      const archiveId = action.payload;
      state.archive.items = state.archive.items.filter(
        (i) => i.archiveId !== archiveId,
      );
      state.archive.total = Math.max(0, state.archive.total - 1);
    });

    // Restore all → clear archive list
    builder.addCase(restoreAllShipments.fulfilled, (state) => {
      state.archive.items = [];
      state.archive.total = 0;
    });
  },
});

export const {
  setListPage,
  setListStatusFilter,
  clearDetail,
  addPendingItem,
  updatePendingItem,
  removePendingItem,
  clearPendingItems,
  patchDraftShipment,
  resetDraftShipment,
  setArchivePage,
  setArchiveStatusFilter,
  setArchiveOrder,
  setArchiveSearch,
} = shipmentTrackerSlice.actions;

export default shipmentTrackerSlice.reducer;
